import type {
    InboxWorkflowItem,
    InboxWorkflowRowOutcomeMap,
    InboxWorkflowState,
    InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import type {
  WorkOrderLetterAddress,
  WorkOrderPrintMode,
  WorkOrderPrintPreviewRow,
} from "../../../../../../../../../../../../../api/kehrblatt/letters"
import {
  buildPrintRowState,
  impersonalRecipientValue,
  manualRecipientValue,
  objectAddressRecipientValue,
} from "../../../_shared/_logic/print-selection"
import type {
  PrintWorkflowRowBinding,
  WorkOrderPrintRowState,
} from "../../../_shared/_logic/print-types"
import type {
  LetterRecipientDecision,
  LetterSelectionDecision,
  LetterSelectionDraft,
  LetterSelectionPreviewPayload,
  LetterSelectionPreviewRowContext,
  LetterSelectionRow,
} from "../contract"

const STEP_KEY = "select_appointment_letters_for_delivery"

const recordValue = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null

const textValue = (value: unknown): string | null => {
  if (typeof value !== "string") return null

  const normalized = value.trim()

  return normalized || null
}

const printMode = (value: unknown): WorkOrderPrintMode | null =>
  value === "PERSONAL_LETTER" || value === "IMPERSONAL_LETTER" || value === "GENERIC_KLEBEZETTEL"
    ? value
    : null

const addressValue = (value: unknown): WorkOrderLetterAddress | null => {
  const source = recordValue(value)

  if (!source || !Array.isArray(source.lines)) return null

  const label = textValue(source.label)
  const lines = source.lines.every((entry) => typeof entry === "string")
    ? source.lines.map(String)
    : null

  if (!label || !lines) return null

  const optional = (entry: unknown) => (entry == null ? null : textValue(entry))

  return {
    label,
    lines,
    street: optional(source.street),
    house_number: optional(source.house_number),
    zip: optional(source.zip),
    city: optional(source.city),
    country: optional(source.country),
    address_extra: optional(source.address_extra),
  }
}

const recipientDecision = (value: unknown): LetterRecipientDecision | null => {
  const source = recordValue(value)
  const kind = textValue(source?.kind)

  if (!source || !kind) return null

  if (kind === "existing_contact") {
    const customerId = textValue(source.customer_id)
    const contactPointId = textValue(source.contact_point_id)

    return customerId && contactPointId
      ? {
          kind,
          customer_id: customerId,
          contact_point_id: contactPointId,
        }
      : null
  }

  if (kind === "customer_at_property_address") {
    const customerId = textValue(source.customer_id)
    const name = source.name == null ? undefined : textValue(source.name)

    return customerId && (source.name == null || name)
      ? { kind, customer_id: customerId, name: name ?? undefined }
      : null
  }

  if (kind === "manual_address") {
    const name = textValue(source.name)
    const address = addressValue(source.address)

    return name && address ? { kind, name, address } : null
  }

  if (kind === "property_address") return { kind }

  return null
}

export const parseLetterSelectionDecision = (value: unknown): LetterSelectionDecision | null => {
  const source = recordValue(value)

  if (!source) return null

  const itemId = textValue(source.letter_delivery_batch_item_id)

  if (itemId && source.action === "ignore")
    return { action: "ignore", letter_delivery_batch_item_id: itemId }

  const mode = printMode(source.print_mode)
  const recipient = recipientDecision(source.recipient)

  return itemId && mode && recipient
    ? {
        letter_delivery_batch_item_id: itemId,
        print_mode: mode,
        recipient,
      }
    : null
}

const projectedStep = (item: InboxWorkflowItem, stepKey: string) =>
  item.steps.find((candidate) => candidate.step_key === stepKey) ?? null

const relevantRequirements = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

const itemIsResolved = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = projectedStep(item, step.step_key)

  if (projected?.status === "resolved") return true

  const requirements = relevantRequirements(item, step)

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const itemIsOpen = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = projectedStep(item, step.step_key)

  if (projected) return projected.status !== "resolved" && projected.status !== "skipped"

  return relevantRequirements(item, step).some((requirement) =>
    item.unresolved_requirements.includes(requirement),
  )
}

const outputForStep = (item: InboxWorkflowItem) => projectedStep(item, STEP_KEY)?.output ?? {}

const emptyPrintRow = (actionCaseId: string): WorkOrderPrintPreviewRow => ({
  work_order_id: actionCaseId,
  property_label: "Vorschaudaten fehlen",
  default_mode: "IMPERSONAL_LETTER",
  available_modes: [],
  recipient_options: [],
})

const printRowWithDurableSelection = (
  print: WorkOrderPrintPreviewRow,
  selection: LetterSelectionDecision | null,
): WorkOrderPrintPreviewRow => {
  if (!selection || selection.action === "ignore") return print

  const recipient = selection.recipient
  const existingRecipient = recipient.kind === "existing_contact" ? recipient : null
  const objectRecipient = recipient.kind === "customer_at_property_address" ? recipient : null
  const hasMode = print.available_modes.some((option) => option.mode === selection.print_mode)
  const hasRecipient = existingRecipient
    ? print.recipient_options.some(
        (option) =>
          option.customer_id === existingRecipient.customer_id &&
          option.contact_point_id === existingRecipient.contact_point_id,
      )
    : true

  return {
    ...print,
    default_mode: selection.print_mode,
    available_modes: hasMode
      ? print.available_modes
      : [...print.available_modes, { mode: selection.print_mode, label: "Gespeicherte Druckart" }],
    recipient_options:
      existingRecipient && !hasRecipient
        ? [
            ...print.recipient_options,
            {
              customer_id: existingRecipient.customer_id,
              contact_point_id: existingRecipient.contact_point_id,
              label: "Gespeicherter Empfänger",
              postal_address: null,
            },
          ]
        : print.recipient_options,
    selected_recipient_customer_id:
      existingRecipient?.customer_id ?? objectRecipient?.customer_id ?? null,
    selected_recipient_contact_point_id: existingRecipient?.contact_point_id ?? null,
    property_address:
      recipient.kind === "manual_address" ? recipient.address : print.property_address,
  }
}

const rowBinding = (
  actionCaseId: string,
  context: LetterSelectionPreviewRowContext | null,
  disabled: boolean,
  disabledReason: string | null,
): PrintWorkflowRowBinding | undefined =>
  context
    ? {
        id: actionCaseId,
        workOrderId: context.work_order_id,
        contactOrderStepId: context.contact_order_step_id,
        recipientCustomerId: context.current_recipient_customer_id,
        recipientContactPointId: context.current_recipient_contact_point_id,
        recipientSource: context.current_recipient_source,
        recipientName: context.current_recipient_name,
        defaultMode: context.current_print_mode,
        letterDeliveryBatchItemId: context.letter_delivery_batch_item_id,
        actionCaseId,
        disabled,
        disabledReason,
      }
    : undefined

const rowStatusLabel = ({
  outputError,
  available,
  resolved,
  outcome,
}: {
  outputError: string | null
  available: boolean
  resolved: boolean
  outcome: InboxWorkflowRowOutcomeMap[string] | null
}) => {
  if (outcome?.status === "failed") return "Prüfung nötig"

  if (outputError) return "Datenfehler"

  if (resolved || outcome?.status === "success") return "Erledigt"

  return available ? "Offen" : "Nicht verfügbar"
}

const durableOutputError = "Gespeicherte Briefauswahl fehlt oder ist ungültig."

const buildRow = ({
  item,
  step,
  context,
  printPreview,
  previewError,
  previewLoading,
  outcome,
}: {
  item: InboxWorkflowItem
  step: InboxWorkflowStep
  context: LetterSelectionPreviewRowContext | null
  printPreview: WorkOrderPrintPreviewRow | null
  previewError: string | null
  previewLoading: boolean
  outcome: InboxWorkflowRowOutcomeMap[string] | null
}): LetterSelectionRow => {
  const actionCaseId = item.action_case.id
  const resolved = itemIsResolved(item, step)
  const open = itemIsOpen(item, step)
  const selection = parseLetterSelectionDecision(outputForStep(item))
  const outputError = resolved && !selection ? durableOutputError : null
  const missingContextReason =
    !open || (context && printPreview)
      ? null
      : (previewError ?? (previewLoading ? "Vorschau wird geladen." : "Vorschaudaten fehlen."))
  const disabledReason =
    outcome?.error ??
    outputError ??
    missingContextReason ??
    (open ? printPreview?.disabled_reason : null) ??
    item.error ??
    (!open ? (resolved ? "Bereits abgeschlossen." : "Dieser Schritt ist nicht offen.") : null)
  const disabled =
    !item.compatible ||
    !open ||
    Boolean(open && (!context || !printPreview)) ||
    Boolean(outputError) ||
    outcome?.status === "success"
  const previewPrint = printPreview
    ? {
        ...printPreview,
        default_mode: context?.current_print_mode ?? printPreview.default_mode,
        selected_recipient_customer_id:
          context?.current_recipient_customer_id ?? printPreview.selected_recipient_customer_id,
        selected_recipient_contact_point_id:
          context?.current_recipient_contact_point_id ??
          printPreview.selected_recipient_contact_point_id,
      }
    : emptyPrintRow(actionCaseId)
  const print = printRowWithDurableSelection(previewPrint, resolved ? selection : null)

  return {
    ...print,
    appointment: context?.appointment,
    communication_presentation: context?.communication_presentation,
    id: actionCaseId,
    actionCaseId,
    workflowItem: item,
    step: projectedStep(item, step.step_key),
    rowOutcome: outcome,
    isResolved: resolved,
    isOpen: open,
    isCompatible: item.compatible,
    letterDeliveryBatchItemId: resolved
      ? (selection?.letter_delivery_batch_item_id ?? null)
      : (selection?.letter_delivery_batch_item_id ??
        context?.letter_delivery_batch_item_id ??
        null),
    selection,
    recipientReviewReason: context?.recipient_review_reason ?? null,
    binding: rowBinding(actionCaseId, context, disabled, disabledReason),
    disabled,
    disabledReason,
    statusLabel:
      selection?.action === "ignore"
        ? "Bewusst ignoriert"
        : rowStatusLabel({
            outputError,
            available: Boolean(
              context && printPreview && !printPreview.disabled_reason && item.compatible,
            ),
            resolved,
            outcome,
          }),
    statusDetail:
      outcome?.error ||
      outcome?.detail ||
      outputError ||
      missingContextReason ||
      context?.recipient_review_reason ||
      item.error ||
      null,
    searchText: [
      print.work_order_label,
      print.order_number,
      print.order_type_internal_label,
      print.property_label,
      print.unit_label,
      print.appointment_label,
      ...print.recipient_options.map((option) => option.label),
    ]
      .filter(Boolean)
      .join(" "),
  }
}

export const buildLetterSelectionRows = ({
  workflow,
  step,
  preview,
  rowOutcomes,
  previewLoading = false,
  previewError = null,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  preview: LetterSelectionPreviewPayload | null
  rowOutcomes: InboxWorkflowRowOutcomeMap
  previewLoading?: boolean
  previewError?: string | null
}): LetterSelectionRow[] =>
  workflow.items.map((item) => {
    const actionCaseId = item.action_case.id
    const context = preview?.row_context_by_action_case_id[actionCaseId] ?? null

    return buildRow({
      item,
      step,
      context,
      printPreview: context
        ? (preview?.shared.print_previews_by_id[context.print_preview_id] ?? null)
        : null,
      previewError: preview?.row_errors_by_action_case_id[actionCaseId] ?? previewError,
      previewLoading,
      outcome: rowOutcomes[actionCaseId] ?? null,
    })
  })

const stateFromSelection = (
  row: LetterSelectionRow,
  selection: LetterSelectionDecision,
): WorkOrderPrintRowState => {
  if (selection.action === "ignore") return { ...buildPrintRowState(row), ignored: true }

  const base = buildPrintRowState(row, selection.print_mode)
  const recipient = selection.recipient

  if (recipient.kind === "existing_contact") {
    const option = row.recipient_options.find(
      (candidate) =>
        candidate.customer_id === recipient.customer_id &&
        candidate.contact_point_id === recipient.contact_point_id,
    )

    return {
      ...base,
      mode: selection.print_mode,
      recipientValue: `${recipient.customer_id}:${recipient.contact_point_id}`,
      recipientCustomerId: recipient.customer_id,
      recipientContactPointId: recipient.contact_point_id,
      recipientSource: "CUSTOMER_CONTACT_ADDRESS",
      recipientName: option?.label ?? null,
      recipientAddress: option?.postal_address ?? null,
      personalConfigConfirmed: true,
    }
  }

  if (recipient.kind === "customer_at_property_address") {
    return {
      ...base,
      mode: selection.print_mode,
      recipientValue: objectAddressRecipientValue,
      recipientCustomerId: recipient.customer_id,
      recipientContactPointId: null,
      recipientSource: "OBJECT_ADDRESS",
      recipientName: recipient.name ?? row.binding?.recipientName ?? null,
      recipientAddress: row.property_address,
      propertyAddress: row.property_address,
      personalConfigConfirmed: true,
    }
  }

  if (recipient.kind === "manual_address") {
    return {
      ...base,
      mode: selection.print_mode,
      recipientValue: manualRecipientValue,
      recipientCustomerId: null,
      recipientContactPointId: null,
      recipientSource: "MANUAL_ADDRESS",
      recipientName: recipient.name,
      recipientAddress: recipient.address,
      personalConfigConfirmed: true,
    }
  }

  return {
    ...base,
    mode: selection.print_mode,
    recipientValue: impersonalRecipientValue,
    recipientCustomerId: null,
    recipientContactPointId: null,
    recipientSource: "IMPERSONAL",
    recipientName: null,
    recipientAddress: null,
    personalConfigConfirmed: true,
  }
}

export const createLetterSelectionDraft = (row: LetterSelectionRow): LetterSelectionDraft => ({
  ...(row.selection
    ? stateFromSelection(row, row.selection)
    : {
        ...buildPrintRowState(row),
        ...(row.recipientReviewReason
          ? {
              mode: "PERSONAL_LETTER" as const,
              recipientValue: "",
              recipientCustomerId: null,
              recipientContactPointId: null,
              recipientName: null,
              recipientAddress: null,
              recipientSource: "CUSTOMER_CONTACT_ADDRESS" as const,
            }
          : {}),
        personalConfigConfirmed: !row.recipientReviewReason,
      }),
})
